import { PBROption, RPDXImportOption } from '@/app/assets/actions/importOptions';
import { PBROptionField } from '@/app/assets/components/upload/atoms/PBROptionField';
import { ScrollableModal } from '@/app/components/modal/ScrollableModal';
import { Alert, Button, Center, Group, Loader, Stack, Tabs, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { CADOptionField } from '../atoms/CADOptionsField';

interface ImportSettingsModalProps {
  opened: boolean;
  close: () => void;
  pbrFiles: string[];
  CADFiles: string[];
  pbrOptions: PBROption[];
  pbrOptionsForm: UseFormReturnType<Record<string, any>>;
  CADOptions: RPDXImportOption[];
  CADOptionsForm: UseFormReturnType<any>;
  onSubmit: () => void;
  isError: boolean;
  isLoading: boolean;
  shouldConfigureAdditionalCADOptions: boolean;
}

export function ImportSettingsModal({ opened, close, onSubmit, ...props }: ImportSettingsModalProps) {
  return (
    <ScrollableModal
      opened={opened}
      size={600}
      title='Import settings'
      onClose={close}
      nonScrollableBottom={
        <Group justify='flex-end' h={60} gap='xs'>
          <Button variant='default' onClick={close}>
            Close
          </Button>
          <Button disabled={props.isLoading || props.isError} onClick={onSubmit}>
            Upload
          </Button>
        </Group>
      }
    >
      <ModalContent {...props} />
    </ScrollableModal>
  );
}

const ModalContent = ({
  pbrFiles,
  CADFiles,
  pbrOptions,
  pbrOptionsForm,
  CADOptions,
  CADOptionsForm,
  isError,
  isLoading,
  shouldConfigureAdditionalCADOptions,
}: Omit<ImportSettingsModalProps, 'opened' | 'close' | 'onSubmit'>) => {
  if (isError) {
    return (
      <Alert color='red'>
        Seems like an error occurred while fetching PBR settings! Try again later. If error persists, please contact the
        customer service!
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  const hasPbrFiles = pbrFiles.length > 0;

  if (hasPbrFiles && shouldConfigureAdditionalCADOptions) {
    return (
      <Tabs defaultValue='pbr'>
        <Tabs.List>
          <Tabs.Tab value='pbr'>.max files</Tabs.Tab>
          <Tabs.Tab value='cad'>CAD Files</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value='pbr'>
          <PBRConfig pbrFiles={pbrFiles} pbrOptionsForm={pbrOptionsForm} pbrOptions={pbrOptions} />
        </Tabs.Panel>
        <Tabs.Panel value='cad'>
          <CADConfig CADFiles={CADFiles} CADOptionsForm={CADOptionsForm} CADOptions={CADOptions} />
        </Tabs.Panel>
      </Tabs>
    );
  }

  if (shouldConfigureAdditionalCADOptions) {
    return <CADConfig CADFiles={CADFiles} CADOptionsForm={CADOptionsForm} CADOptions={CADOptions} />;
  }

  return <PBRConfig pbrFiles={pbrFiles} pbrOptionsForm={pbrOptionsForm} pbrOptions={pbrOptions} />;
};

const PBRConfig = ({
  pbrFiles,
  pbrOptions,
  pbrOptionsForm,
}: {
  pbrFiles: string[];
  pbrOptions: PBROption[];
  pbrOptionsForm: UseFormReturnType<Record<string, any>>;
}) => {
  return (
    <Stack gap='xs' mt='md'>
      <Text fw={600}>Choose settings for all .max files</Text>
      <Stack gap={0}>
        <Text c='gray.6'>The settings will be applied to all .max files in this upload:</Text>
        {pbrFiles.map((f, i) => (
          <Text key={f} fw={600}>{`${f}${i !== pbrFiles.length - 1 ? ',' : ''}`}</Text>
        ))}
      </Stack>
      <Stack gap='xs' mt='md'>
        {pbrOptions.map((o) => (
          <PBROptionField key={o.display_name} option={o} form={pbrOptionsForm} />
        ))}
      </Stack>
    </Stack>
  );
};

const CADConfig = ({
  CADFiles,
  CADOptions,
  CADOptionsForm,
}: {
  CADFiles: string[];
  CADOptions: RPDXImportOption[];
  CADOptionsForm: UseFormReturnType<Record<string, any>>;
}) => (
  <Stack gap='xs' mt='md'>
    <Text fw={600}>Choose settings for all CAD files</Text>
    <Stack gap={0}>
      <Text c='gray.6'>The settings will be applied to all CAD files in this upload:</Text>
      {CADFiles.map((f, i) => (
        <Text key={f} fw={600}>{`${f}${i !== CADFiles.length - 1 ? ',' : ''}`}</Text>
      ))}
    </Stack>
    <Stack gap='xs' mt='md'>
      {CADOptions.map((o) => (
        <CADOptionField key={o.schema_path} option={o} form={CADOptionsForm} />
      ))}
    </Stack>
  </Stack>
);
